/**
 * Paths for the v2 customer API, served by cdk-salesforce.
 *
 * These are the Salesforce-backed replacements for the Chargebee endpoints that sls-subscriptions
 * still serves at /customer/subscriptions, /customer/invoices and so on. Both have to coexist until
 * Chargebee retires, so the two live under different roots — but the root is a version rather than
 * the vendor's name, which means retiring it later is an ordinary deprecation instead of a rename.
 *
 * Everything is built from V2_ROOT so that day is a one-line change here.
 */
const V2_ROOT = "/customer/v2"

export const subscriptionsPath = id => (id ? `${V2_ROOT}/subscriptions/${id}` : `${V2_ROOT}/subscriptions`)

export const trialsPath = id => (id ? `${V2_ROOT}/trials/${id}` : `${V2_ROOT}/trials`)

export const checkoutPath = () => `${V2_ROOT}/checkout`

export const catalogPath = currency => `${V2_ROOT}/catalog?currency=${currency}`

export const taxQuotesPath = () => `${V2_ROOT}/tax_quotes`

// The Salesforce quote a purchase is placed from. Opened before payment, then polled until
// Salesforce has priced it.
export const quotesPath = id => (id ? `${V2_ROOT}/quotes/${id}` : `${V2_ROOT}/quotes`)

// The card payment, taken through the Billing Engine (which fronts Stripe). Opened with a POST,
// confirmed in the browser with Stripe Elements, then polled until the engine reports it settled —
// the customer coming back from 3DS does not by itself mean the charge succeeded.
export const paymentTransactionsPath = (reference, currency) => (reference
  ? `${V2_ROOT}/payment-transactions/${encodeURIComponent(reference)}?currency=${encodeURIComponent(currency)}`
  : `${V2_ROOT}/payment-transactions`)

// GET (with currency): stored cards + Stripe publishable key. POST (no query): save a new card.
export const paymentMethodsPath = ({ currency, quoteId, subscriptionId } = {}) => {
  if (!currency) {
    return `${V2_ROOT}/payment-methods`
  }
  const params = new URLSearchParams({ currency })
  if (quoteId) {
    params.set("quote_id", quoteId)
  }
  if (subscriptionId) {
    params.set("subscription_id", subscriptionId)
  }
  return `${V2_ROOT}/payment-methods?${params}`
}

// One saved card (DELETE), and making it the customer's default (POST).
export const paymentMethodPath = id => `${V2_ROOT}/payment-methods/${encodeURIComponent(id)}`

export const paymentMethodDefaultPath = id => `${paymentMethodPath(id)}/default`

// The billing address of the customer's Salesforce Account (My Account). 404 when not on RCA.
export const billingDetailsPath = () => `${V2_ROOT}/billing_details`

// The invoices of the customer's Salesforce subscriptions: the list, and one invoice's PDF.
export const invoicesPath = id => (id ? `${V2_ROOT}/invoices/${encodeURIComponent(id)}` : `${V2_ROOT}/invoices`)
